import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePassage } from '@/hooks/use-passage';
import { useSettingsStore } from '@/stores/settingsStore';
import { Loader2 } from 'lucide-react';
import { useAttendeeSearchResult } from '@/features/desktop/api/get-attendee-search-result';
import { useAttendeeFilterResult } from '@/features/desktop/api/get-attendee-fllter-result';
import { AttendeeSearchResult } from '@/features/desktop/types/attendee-search-result';
import { useOnsiteStats } from '@/features/desktop/api/get-onsite-stats';
import { getLabelValue } from '@/utils/getLabelValue';
import { useNavigate, useSearchParams } from 'react-router-dom';

type SearchListResultProps = {
  searchKey: string;
  selectedStatus: string | null;
  sortBy: 'firstName' | 'lastName' | 'companyName' | 'email';
  sortDirection: 'asc' | 'desc';
};

export function SearchListResult({ searchKey, selectedStatus, sortBy, sortDirection }: SearchListResultProps) {
  const eventId = useSettingsStore(state => state.eventId) ?? 0;
  const breakoutId = useSettingsStore(state => state.breakoutId);
  const direction = useSettingsStore(state => state.direction);
  const { pass, proccessingBadgeNo } = usePassage();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const eventIdFromQuery = searchParams.get('eventId');

  const effectiveEventId = eventIdFromQuery ? Number(eventIdFromQuery) : breakoutId ?? eventId;

  const filterQuery = useAttendeeFilterResult({
    eventId,
    status: selectedStatus || 'booked',
    breakoutId: effectiveEventId,
    config: {
      enabled: !!eventId,
    },
  });

  const searchQuery = useAttendeeSearchResult({
    eventId,
    searchKey,
    breakoutId: effectiveEventId,
    status: selectedStatus,
    config: {
      enabled: !!searchKey,
    },
  });

  const { data: stats } = useOnsiteStats({
    eventId,
    breakoutId: effectiveEventId,
    config: {
      enabled: true,
    },
  });

  const attendees: AttendeeSearchResult[] = searchKey
    ? selectedStatus
      ? searchQuery.data?.filter(a => filterQuery.data?.some(b => b.badgeNo === a.badgeNo)) || []
      : searchQuery.data || []
    : filterQuery.data || [];

  const sortedAttendees = [...attendees].sort((a, b) => {
    const fieldA = (a[sortBy] ?? '').toString().toLowerCase();
    const fieldB = (b[sortBy] ?? '').toString().toLowerCase();

    if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleCheckInOut = (badgeNo: string) => {
    pass(badgeNo);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        {(searchQuery.isLoading || filterQuery.isLoading) && (
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {sortedAttendees.length === 0 && !searchQuery.isLoading && !filterQuery.isLoading && <p className="text-center text-muted-foreground">{getLabelValue('NoAttendeesFound')}.</p>}
        {sortedAttendees.map(attendee => {
          const isOnsite = stats?.onsite?.[attendee.badgeNo];

          return (
            <Card key={attendee.badgeNo} onClick={() => navigate(`/checkin/attendee-details?badgeNo=${attendee.badgeNo}`)}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                  {isOnsite && (
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="relative flex justify-center items-center">
                        <span className="bg-emerald-500 w-2 h-2 rounded-full animate-ping absolute" />
                        <span className="bg-emerald-500 w-2 h-2 rounded-full absolute" />
                      </span>
                    </span>
                  )}
                    <p className="font-semibold truncate ">
                      {attendee.firstName} {attendee.lastName}
                    </p>
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    <p className="text-sm text-muted-foreground truncate">{attendee.companyName || ''}</p>
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    <p className="text-sm text-muted-foreground truncate">{attendee.personTypes?.length > 0 ? attendee.personTypes.map(pt => pt.name).join(', ') : ''}</p>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-4">
                    <Button
                      onClick={e => {
                        e.stopPropagation();
                        handleCheckInOut(attendee.badgeNo);
                      }}
                      disabled={proccessingBadgeNo === attendee.badgeNo}
                      variant={direction === 'In' ? 'default' : 'destructive'}
                      className="flex-shrink-0 self-center max-w-24"
                    >
                      {proccessingBadgeNo === attendee.badgeNo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : direction === 'In' ? (
                        getLabelValue('CheckInAction')
                      ) : (
                        getLabelValue('CheckOutAction')
                      )}
                    </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
